import { useEffect, useState } from "react";
import type { AnswerMark, FiftyFiftyAnimation, Submission } from "@quizshow/protocol";

export function isFiftyFiftyComplete(animation: FiftyFiftyAnimation | undefined, now = Date.now()): boolean {
  if (!animation) return true;
  return animation.skipped || Date.parse(animation.startedAt) + animation.durationMs <= now;
}

export function useFiftyFiftyComplete(animation: FiftyFiftyAnimation | undefined): boolean {
  const [complete, setComplete] = useState(() => isFiftyFiftyComplete(animation));

  useEffect(() => {
    setComplete(isFiftyFiftyComplete(animation));
    if (!animation || animation.skipped) return;
    const remaining = Math.max(0, Date.parse(animation.startedAt) + animation.durationMs - Date.now());
    const timer = window.setTimeout(() => setComplete(true), remaining);
    return () => window.clearTimeout(timer);
  }, [animation?.startedAt, animation?.durationMs, animation?.skipped]);

  return complete;
}

export function answerPresentationClasses(
  optionId: string,
  hiddenOptionIds: string[],
  answerMarks: AnswerMark[],
  solutionOptionIds: string[],
  animation: FiftyFiftyAnimation | undefined,
  animationComplete: boolean,
): string {
  const mark = answerMarks.find((entry) => entry.optionId === optionId);
  const eliminated = hiddenOptionIds.includes(optionId) && animationComplete;
  const roulette =
    Boolean(animation) &&
    !animationComplete &&
    (animation?.removedOptionIds.includes(optionId) || !hiddenOptionIds.includes(optionId));
  return [
    eliminated ? "answer-card--eliminated" : "",
    roulette ? "answer-card--roulette" : "",
    mark ? `answer-card--mark-${mark.status}` : "",
    solutionOptionIds.includes(optionId) ? "answer-card--solution" : "",
  ]
    .filter(Boolean)
    .join(" ");
}

export function participantSubmissionMarks(
  releasedMarks: AnswerMark[],
  submission: Submission | undefined,
): AnswerMark[] {
  if (!submission || submission.answerMode === "free-text" || submission.evaluation === "pending") {
    return releasedMarks;
  }
  const status =
    submission.evaluation === "correct"
      ? "correct"
      : submission.evaluation === "incorrect"
        ? "incorrect"
        : "mentioned";
  const ownMarks = submission.optionIds.map((optionId) => ({
    optionId,
    status,
    releasedToParticipants: true,
    releasedToStream: false,
  } satisfies AnswerMark));
  return [
    ...releasedMarks.filter((mark) => !submission.optionIds.includes(mark.optionId)),
    ...ownMarks,
  ];
}
